const React = require("react");
const { useSearchParams } = require("react-router-dom");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, ResourceState, ProductCard, PaginationBar } = require("./ui");
const { FilterBar } = require("./FilterBar");
const { ShipFromFilter } = require("./ShipFromFilter");
const { CJDropshippingViewModal } = require("./CJDropshippingViewModal");
const { CJSyncModal } = require("./sync-wizard/CJSyncModal");
const { CJSyncActionModal } = require("./CJSyncActionModal");
const { DefaultLoadingManager } = require("./DefaultLoadingManager");
const { PASSWORD_CONFIRMATION_ACTIONS } = require("../../../src/includes/pageInside/sync/passwordConfirmation.js");
const h = React.createElement;

const PAGE_SIZE = 40;
const INITIAL_CJ_MODAL_STATE = Object.freeze({ viewProductId: null, syncTarget: null, deleteTarget: null, deletingId: null, error: "" });
const cjModalReducer = (state, action) => {
  switch (action.type) {
    case "view": return { ...state, viewProductId: action.productId };
    // Ferme aussi la vue produit (Info/Sync & Compare/...) : ouverte depuis
    // son propre bouton "Import to Elakay" (voir CJDropshippingViewModal.js),
    // le wizard ne doit jamais s'empiler par-dessus elle.
    case "sync": return { ...state, viewProductId: null, syncTarget: action.target, error: "" };
    case "delete": return { ...state, deleteTarget: action.product, error: "" };
    case "deleting": return { ...state, deletingId: action.productId, error: "" };
    case "error": return { ...state, error: action.error, deletingId: null };
    case "deleted": return { ...state, deleteTarget: null, deletingId: null, error: "" };
    case "close-view": return { ...state, viewProductId: null };
    case "close-sync": return { ...state, syncTarget: null };
    case "close-delete": return state.deletingId ? state : { ...state, deleteTarget: null, error: "" };
    default: return state;
  }
};

const INITIAL_FILTERS = {
  keyword: "",
  supplierId: "",
  categoryId: "",
  countryCode: "",
  // Ship From (warehouse origin, see ShipFromFilter.js) -- distinct from
  // `countryCode` above, which is the shipping-destination filter already
  // exposed by FilterBar.js. Reuses the exact field name already persisted
  // on DropshippingProduct (see server_elakay's productQueryBuilder.js).
  fromCountryCode: "",
  status: "",
  minPrice: "",
  maxPrice: "",
  minDeliveryDays: "",
  maxDeliveryDays: "",
  sort: "newest",
};

// Miroir léger côté client de la validation backend (validate.js) : on
// n'envoie jamais une requête min>max, plutôt que de laisser le serveur la
// rejeter en 400 après coup.
const filtersAreValid = (filters) => {
  const minPrice = filters.minPrice === "" ? null : Number(filters.minPrice);
  const maxPrice = filters.maxPrice === "" ? null : Number(filters.maxPrice);
  if (minPrice !== null && maxPrice !== null && minPrice > maxPrice) return false;
  const minDelivery = filters.minDeliveryDays === "" ? null : Number(filters.minDeliveryDays);
  const maxDelivery = filters.maxDeliveryDays === "" ? null : Number(filters.maxDeliveryDays);
  if (minDelivery !== null && maxDelivery !== null && minDelivery > maxDelivery) return false;
  return true;
};

// `embedded` (utilisé par le tab "Products" du modal fournisseur, voir
// CJDropshippingViewModal.js) saute le PageHeader interne pour ne pas
// dupliquer un titre déjà porté par le Modal.Title englobant.
const ProductCollectionPage = ({ title, description, imported, supplierId, embedded = false }) => {
  const { api, t } = useCJDropshipping();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cjModalState, dispatchCJModal] = React.useReducer(cjModalReducer, INITIAL_CJ_MODAL_STATE);
  const { viewProductId, syncTarget, deletingId, deleteTarget, error: deleteError } = cjModalState;
  const [page, setPage] = React.useState(1);
  const [pageTransition, setPageTransition] = React.useState(false);
  const [showDefaultManager, setShowDefaultManager] = React.useState(false);
  const [collectionMessage, setCollectionMessage] = React.useState("");

  // `filters` = état "live" (chaque frappe/clic le met à jour immédiatement,
  // pour que les champs contrôlés restent réactifs) ; `committedFilters` =
  // ce qui déclenche réellement un fetch. Seul le champ recherche texte
  // passe par un debounce avant de committer -- les selects/le tri/le reset
  // committent tout de suite (discret vs continu : un select n'a pas besoin
  // d'attendre 300ms, contrairement à la frappe au clavier).
  const [filters, setFilters] = React.useState(() => ({
    ...INITIAL_FILTERS,
    categoryId: searchParams.get("categoryId") || "",
  }));
  const [committedFilters, setCommittedFilters] = React.useState(filters);
  const debounceRef = React.useRef(null);

  const commitIfValid = (next) => {
    if (filtersAreValid(next)) setCommittedFilters(next);
  };

  const handleFilterChange = (partial) => {
    const next = { ...filters, ...partial };
    setFilters(next);
    const isKeywordOnly = Object.keys(partial).length === 1 && Object.prototype.hasOwnProperty.call(partial, "keyword");
    clearTimeout(debounceRef.current);
    if (isKeywordOnly) {
      debounceRef.current = setTimeout(() => commitIfValid(next), 300);
    } else {
      commitIfValid(next);
    }
  };

  const handleReset = () => {
    clearTimeout(debounceRef.current);
    setFilters(INITIAL_FILTERS);
    setCommittedFilters(INITIAL_FILTERS);
    if (searchParams.get("categoryId")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("categoryId");
      setSearchParams(nextParams);
    }
  };

  const resource = useDropshippingResource(
    () => supplierId
      ? api.getSupplierProducts(supplierId, { ...committedFilters, page, limit: PAGE_SIZE })
      : api.getProducts({ ...committedFilters, imported, refresh: imported ? undefined : "true", page, limit: PAGE_SIZE }),
    [api, committedFilters, imported, supplierId, page]
  );
  const defaultResource = useDropshippingResource(
    () => (!imported && !supplierId ? api.getDefaultProducts() : Promise.resolve({ products: [], configured: false })),
    [api, imported, supplierId]
  );
  const collectionResource = useDropshippingResource(
    () => (!imported && !supplierId ? api.getDefaultLoading() : Promise.resolve({ configuration: { products: [] }, groups: [], suppliers: [] })),
    [api, imported, supplierId]
  );

  // Les filtres (recherche, fournisseur, catégorie, pays, statut, prix,
  // délai, tri, catalogue importé/supplier) réinitialisent la pagination --
  // sans quoi on pourrait se retrouver sur une page vide.
  React.useEffect(() => { setPage(1); }, [committedFilters, imported, supplierId]);

  // Clic Précédent/Suivant : on vide explicitement la grille (au lieu de
  // laisser les anciens produits affichés pendant le fetch) et on affiche un
  // petit indicateur de chargement scoped à la grille -- sinon un clic sur
  // la pagination ne se "voit" pas (mêmes cartes à l'écran) et donne
  // l'impression que le bouton ne fait rien. Le scroll vers le début du
  // contenu est géré directement par PaginationBar (voir ui.js). `sawLoadingRef`
  // garantit qu'on attend bien le VRAI cycle loading=true -> false du fetch
  // déclenché par ce changement de page avant de considérer la transition
  // terminée (sinon un rendu intermédiaire où `resource.loading` n'est pas
  // encore repassé à true masquerait la transition trop tôt). Le petit délai
  // minimum (250ms) après réception des données évite un flash trop brutal
  // si la réponse revient quasi instantanément.
  const sawLoadingRef = React.useRef(false);
  const goToPage = (updater) => {
    sawLoadingRef.current = false;
    setPageTransition(true);
    setPage(updater);
  };
  React.useEffect(() => {
    if (!pageTransition) return undefined;
    if (resource.loading) { sawLoadingRef.current = true; return undefined; }
    if (!sawLoadingRef.current) return undefined;
    const timer = setTimeout(() => setPageTransition(false), 250);
    return () => clearTimeout(timer);
  }, [resource.loading, pageTransition]);

  const catalogProducts = resource.data?.products || [];
  const hasActiveFilters = Object.entries(committedFilters).some(([key, value]) => key !== "sort" && Boolean(value));
  const showDefaultsFirst = !imported && !supplierId && page === 1 && !hasActiveFilters;
  const products = React.useMemo(() => {
    const merged = showDefaultsFirst
      ? [...(defaultResource.data?.products || []), ...catalogProducts]
      : catalogProducts;
    const seen = new Set();
    return merged.filter((product) => {
      const key = product.externalProductId ? `pid:${String(product.externalProductId).toLowerCase()}` : `sku:${String(product.sku || "").toLowerCase()}`;
      if (!product.externalProductId && !product.sku) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [catalogProducts, defaultResource.data, showDefaultsFirst]);
  const total = resource.data?.total ?? catalogProducts.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const deleteImportedProduct = async (product) => {
    const elakayProductId = product?.elakayProductId;
    if (!elakayProductId || deletingId) return;
    dispatchCJModal({ type: "delete", product });
  };

  const confirmDeleteImportedProduct = async ({ confirmationToken, destructive }) => {
    const product = deleteTarget;
    const elakayProductId = product?.elakayProductId;
    if (!elakayProductId || deletingId) return;
    dispatchCJModal({ type: "deleting", productId: product._id });
    try {
      await api.deleteCJProduct(elakayProductId, { deleteElakayProduct: destructive }, { confirmationToken, confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_SYNC_DELETE });
      await resource.reload();
      dispatchCJModal({ type: "deleted" });
    } catch (error) {
      dispatchCJModal({ type: "error", error: error.message || t("h_cj_delete_product_error", "Unable to delete this product.") });
    }
  };

  const collections = React.useMemo(() => [
    { value: "default", label: t("h_cj_default_products", "Default Products") },
    ...(collectionResource.data?.groups || []).map((group) => ({ value: `group:${group._id}`, label: group.name })),
    ...(collectionResource.data?.suppliers || []).map((supplier) => ({ value: `supplier:${supplier._id}`, label: supplier.name })),
  ], [collectionResource.data, t]);
  const addProductToCollection = async (product, target) => {
    const reference = { productId: product._id, cjPid: product.externalProductId, cjSku: product.sku, name: product.title, image: product.images?.[0] || "" };
    setCollectionMessage("");
    try {
      if (target === "default") {
        const existing = collectionResource.data?.configuration?.products || [];
        await api.updateDefaultLoading({ products: [...existing, reference] });
        await defaultResource.reload();
      } else if (target.startsWith("group:")) await api.addProductGroupProduct(target.slice(6), reference);
      else if (target.startsWith("supplier:")) await api.addPersonalSupplierProduct(target.slice(9), reference);
      await collectionResource.reload();
      setCollectionMessage(t("h_cj_default_added", "Product added to your collection."));
    } catch (addError) { setCollectionMessage(addError.message); }
  };

  return h(React.Fragment, null,
    embedded ? null : h(PageHeader, {
      title,
      description,
      action: h("div", { className: "cjds-row" },
        !imported && !supplierId ? h(Button, { onClick: () => setShowDefaultManager(true) }, t("h_cj_default_manage", "Manage Default Loading")) : null,
        h(Button, { variant: "secondary", onClick: resource.reload }, t("h_cj_refresh", "Refresh"))
      ),
    }),
    showDefaultsFirst && defaultResource.data && !defaultResource.data.configured
      ? h("div", { className: "cjds-state cjds-default-empty" },
          h("h3", null, t("h_cj_default_empty", "No default products configured.")),
          h("p", { className: "cjds-muted" }, t("h_cj_default_empty_desc", "Configure what should appear first. Products are never imported automatically.")),
          h(Button, { onClick: () => setShowDefaultManager(true) }, t("h_cj_default_configure", "Configure Default Loading"))
        )
      : null,
    collectionMessage ? h("div", { className: "cjds-success", role: "status" }, collectionMessage) : null,
    h(FilterBar, { value: filters, onChange: handleFilterChange, onReset: handleReset }),
    // Also shown on the supplier-scoped tab (supplierId set): when this
    // filter is active, getSupplierProducts now serves the cached Mongo read
    // for this supplier instead of CJ's live list (which has no ship-from
    // param) -- see catalog.controller.js#getSupplierProducts.
    h(ShipFromFilter, {
      value: filters.fromCountryCode,
      // Ship From commits immediately (same "discrete" pattern as the
      // supplier/category/status selects above -- see handleFilterChange),
      // no debounce needed since it's never driven by free text typing.
      onChange: (fromCountryCode) => handleFilterChange({ fromCountryCode }),
    }),
    deleteError ? h("div", { className: "cjds-alert", role: "alert" }, deleteError) : null,
    h(ResourceState, {
      // Ne bloque tout l'écran derrière "Loading…" que pour le tout premier
      // chargement (pas encore de `resource.data`). Sans ce garde-fou,
      // ResourceState démonte la grille ET la PaginationBar à CHAQUE
      // changement de page (loading passe à true à chaque clic Suivant/
      // Précédent), ce qui fait disparaître/réapparaître toute la section --
      // perçu comme un bug de la pagination. Les boutons restent désactivés
      // pendant le rechargement via la prop `loading` de PaginationBar
      // ci-dessous, donc le retour visuel de chargement reste présent.
      loading: resource.loading && !resource.data,
      error: resource.error,
      empty: !products.length,
      onRetry: resource.reload,
      t,
      // CJ's bulk product-list API doesn't return real warehouse-origin data
      // per product (see ShipFromFilter.js) -- most cached products only
      // ever get tagged "CN" (CJ's own fallback) or nothing at all, so any
      // other Ship From selection legitimately matches zero products today.
      // Without this, that reads as "the filter is broken" instead of "no
      // match" -- same ResourceState/empty component as everywhere else
      // (see ui.js), just clarified text for this specific, known cause.
      ...(filters.fromCountryCode && !products.length ? {
        emptyTitle: t("h_cj_shipfrom_empty_title", "No products tagged for this country yet"),
        emptyDescription: t(
          "h_cj_shipfrom_empty_desc",
          "CJ doesn't provide real ship-from data for most products in bulk search results yet -- only products you've individually opened (or that CJ already tags \"China\") carry a reliable value. Try \"China\" or switch back to \"All\" to see the full catalog."
        ),
      } : {}),
    },
      h("div", { className: "cjds-grid products" },
        pageTransition
          ? h("div", { className: "cjds-grid-transition", role: "status" },
              t("h_cj_loading", "Loading…"))
          : products.map((product) => h(ProductCard, {
              key: product._id,
              product,
              t,
              onView: (item) => dispatchCJModal({ type: "view", productId: item._id }),
              // Add ET Sync ouvrent désormais le même wizard 3 étapes --
              // AUCUN appel réseau ici (voir CJSyncModal.finalizeCJProductSynchronization,
              // seul endroit autorisé à créer/modifier le produit Elakay).
              // "Add" (mode=import) n'a pas encore d'elakayProductId ; "Sync"
              // (mode=sync, déjà importé) en a toujours un (voir ProductCard).
              onImport: imported ? null : (item) => dispatchCJModal({ type: "sync", target: { mode: "import", dropshippingProductId: item._id } }),
              onSync: (item) => dispatchCJModal({ type: "sync", target: { mode: "sync", dropshippingProductId: item._id, elakayProductId: item.elakayProductId } }),
              onDelete: imported ? deleteImportedProduct : null,
              onAddTo: !imported && !supplierId ? addProductToCollection : null,
              collections,
              busy: deletingId === product._id,
            }))
      ),
      h(PaginationBar, {
        page, pageCount, loading: resource.loading, t,
        onPrevious: () => goToPage((current) => Math.max(1, current - 1)),
        onNext: () => goToPage((current) => Math.min(pageCount, current + 1)),
      })
    ),
    h(CJDropshippingViewModal, {
      show: Boolean(viewProductId),
      onHide: () => dispatchCJModal({ type: "close-view" }),
      kind: "product",
      id: viewProductId,
      // Même action que le bouton "Add" de ProductCard (mode="import") --
      // voir le case "sync" du reducer ci-dessus pour la fermeture de cette
      // vue en même temps.
      onImport: (item) => dispatchCJModal({ type: "sync", target: { mode: "import", dropshippingProductId: item._id } }),
      t,
    }),
    h(CJSyncModal, {
      show: Boolean(syncTarget),
      onHide: () => dispatchCJModal({ type: "close-sync" }),
      mode: syncTarget?.mode,
      dropshippingProductId: syncTarget?.dropshippingProductId,
      elakayProductId: syncTarget?.elakayProductId,
      onSynced: resource.reload,
    }),
    h(CJSyncActionModal, {
      show: Boolean(deleteTarget),
      onHide: () => dispatchCJModal({ type: "close-delete" }),
      mode: "delete",
      title: t("h_cj_delete_sync_title", "Delete CJ synchronization?"),
      subtitle: t("h_cj_delete_sync_subtitle", "Review the consequence before confirming."),
      cjProduct: deleteTarget,
      elakayProduct: deleteTarget?.elakayProductId ? { _id: deleteTarget.elakayProductId, title: deleteTarget.title } : null,
      loading: Boolean(deletingId),
      error: deleteError,
      passwordRequired: true,
      confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_SYNC_DELETE,
      onPasswordConfirm: ({ password, action }) => api.confirmPassword(password, action),
      destructiveOption: t("h_cj_delete_product_data", "Also delete the Elakay product, gallery and attached CJ data (dangerous)"),
      onConfirm: confirmDeleteImportedProduct,
    }),
    h(DefaultLoadingManager, {
      show: showDefaultManager,
      onHide: () => setShowDefaultManager(false),
      onChanged: () => { defaultResource.reload(); collectionResource.reload(); },
    })
  );
};

module.exports = { ProductCollectionPage };
